import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
    ProjectClient,
    GetProjectListResponse,
    UpsertProjectRequest,
    UpsertProjectResponse,
    UpsertComponentRequest,
    ComponentClient,
    GetProjectDetailsResponse,
    ProjectComment,
    ProjectCommentClient,
    MailingListClient,
    UpsertMailingListRequest,
    CraneType,
    PostalAddress,
    DateTimeTba,
    TenderedType,
    ReceivedType,
    UpsertProjectCommentRequest,
} from "../WebApiClient";
import { Observable, combineLatest, BehaviorSubject, merge, of } from "rxjs";
import {
    distinctUntilChanged,
    map,
    switchMap,
    publishReplay,
    refCount,
    shareReplay,
} from "rxjs/operators";
import { Guid } from "../models/Guid";
import { ComponentStatus } from "../models/ComponentStatus";
import { ProjectSearchOptions } from "../models/projectSearchOptions";

export type CraneName = { value: CraneType; name: string };
export type TenderedName = { value: TenderedType; name: string };
export type ReceivedName = { value: ReceivedType; name: string };

@Injectable()
export class ProjectService {
    /** Updates everytime any project updates. When false: updating is in progress. */
    private readonly allTrigger = new BehaviorSubject(true);

    /** Updates whenever any project template updates. When false: updating is in progress. */
    private readonly projectTemplateTrigger = new BehaviorSubject(true);

    private readonly projectTriggers: {
        [projectId: string]: BehaviorSubject<undefined> | undefined;
    } = {};
    private readonly projectsDetailsByProjectId: {
        [projectId: string]:
            | Observable<GetProjectDetailsResponse | undefined>
            | undefined;
    } = {};
    private readonly commentsByProjectId: {
        [projectId: string]:
            | Observable<ProjectComment[] | undefined>
            | undefined;
    } = {};

    public readonly craneTypes: Readonly<readonly CraneName[]> = Object.freeze([
        { value: CraneType.None, name: "None" },
        { value: CraneType.VesselGear, name: "Vessel gear" },
        { value: CraneType.PortCrane, name: "Port cranes" },
        { value: CraneType.MobileCrane, name: "Mobile cranes" },
        { value: CraneType.RoRo, name: "Ro/Ro" },
    ]);

    public readonly tenderedTypes: Readonly<readonly TenderedName[]> =
        Object.freeze([
            { value: TenderedType.Unknown, name: "Unknown" },
            { value: TenderedType.None, name: "None" },
            { value: TenderedType.Date, name: "Date" },
        ]);

    public readonly receivedTypes: Readonly<readonly ReceivedName[]> =
        Object.freeze([
            { value: ReceivedType.Unknown, name: "Unknown" },
            { value: ReceivedType.None, name: "None" },
            { value: ReceivedType.Date, name: "Date" },
        ]);

    public readonly templates: Observable<GetProjectListResponse | undefined> =
        this.projectTemplateTrigger.pipe(
            switchMap((ready) => ready
                ? merge(of(undefined), this.projectClient.getTemplateProjects() )
                : of(undefined)
            ),
        );

    constructor(
        private readonly projectClient: ProjectClient,
        private readonly componentClient: ComponentClient,
        private readonly commentClient: ProjectCommentClient,
        private readonly mailingListClient: MailingListClient,
        private router: Router
    ) {}

    getNewProject() {
        const result = new UpsertProjectRequest({
            projectId: Guid.newGuid(),
            name: "",
            customerIds: [],
            shipName: "",
            caseNumber: "",
            voyageNumber: "",
            from: "",
            to: "",
            expectedArrival: new DateTimeTba({
                dateTime: new Date(),
                timeIsTba: false,
            }),
            expectedDeparture: new DateTimeTba({
                dateTime: new Date(),
                timeIsTba: false,
            }),
            shipper: new PostalAddress({
                name: "",
                address: "",
                postalCode: "",
                city: "",
                country: "",
            }),
            consignee: new PostalAddress({
                name: "",
                address: "",
                postalCode: "",
                city: "",
                country: "",
            }),
            notifyAddress: new PostalAddress({
                name: "",
                address: "",
                postalCode: "",
                city: "",
                country: "",
            }),
            mailingListId: undefined,
            craneType: CraneType.Unknown,
            shoreCraneBooked: undefined,
            importantInformation: "",
            actualDeparture: undefined,
            isPilotBooked: undefined,
            isTugBooked: undefined,
            tenderedType: TenderedType.Unknown,
            receivedType: ReceivedType.Unknown,
        });
        return result;
    }

    /**
     * Gets a list of projects. Will return undefined when the list is updating.
     *
     * @param request
     */
    getProjectsObservable(
        request: Observable<ProjectSearchOptions>
    ): Observable<GetProjectListResponse | undefined> {
        let maxResultsPerPage = 25;
        let o = combineLatest(
            request.pipe(distinctUntilChanged()),
            this.allTrigger
        ).pipe(
            switchMap(([req, isReady]) =>
                isReady
                    ? merge(
                          of(undefined),
                          this.projectClient.getProjects(
                              req.before,
                              req.after,
                              req.customerId,
                              req.searchTerm,
                              (req.page - 1) * maxResultsPerPage,
                              maxResultsPerPage,
                              undefined,
                              req.descending,
                              req.statuses,
                              req.isProjectTemplate
                          )
                      )
                    : of(undefined)
            )
        );

        return o;
    }

    isValid(req: UpsertProjectRequest): boolean {
        if (!req) return false;
        if (!req.name) return false;
        if (!req.projectId) return false;
        if (!req.caseNumber) return false;
        if (!req.customerIds) return false;
        if (req.customerIds.length === 0) return false;
        if (!req.expectedArrival) return false;
        if (!req.expectedDeparture) return false;
        if (!req.from) return false;
        if (!req.to) return false;
        if (!req.shipName) return false;
        if (!req.voyageNumber) return false;
        if (
            req.craneType === CraneType.Unused ||
            req.craneType === CraneType.Unknown
        )
            return false;
        if (req.mailingListId == undefined) return false;
        if (!req.customerRep) return false;
        if (!req.stevedoreForemanId) return false;
        if (!req.opcAgentId) return false;
        if (!req.cargoDescription) return false;
        if (req.shoreCraneBooked == undefined || req.shoreCraneBooked == null)
            return false;
        if (req.tenderedType === TenderedType.Unsued) return false;
        if (req.receivedType === ReceivedType.Unsued) return false;
        if (!req.shipWeight) return false;
        if (req.usingShorePower == undefined) return false;
        return true;
    }

    isTemplateValid(req: UpsertProjectRequest): boolean {
        if (!req) return false;
        if (!req.projectId) return false;
        if (req.tenderedType === TenderedType.Unsued) return false;
        if (req.receivedType === ReceivedType.Unsued) return false;
        return true;
    }

    async upsertProject(
        request: UpsertProjectRequest
    ): Promise<UpsertProjectResponse> {
        this.allTrigger.next(false);
        if (!this.isValid(request)) throw new Error("Invalid update request");

        if (!request.mailingListId) {
            let emptyMailingList = new UpsertMailingListRequest({
                mailingListId: Guid.newGuid(),
                mailReceivers: [],
            });
            await this.mailingListClient
                .upsertMailingList(emptyMailingList)
                .toPromise();
            request.mailingListId = emptyMailingList.mailingListId;
        }

        var response = await this.projectClient
            .upsertProject(request)
            .toPromise();
        this.reloadProject(request.projectId);
        return response;
    }

    async upsertTemplate(
        request: UpsertProjectRequest
    ): Promise<UpsertProjectResponse> {
        this.allTrigger.next(false);
        this.projectTemplateTrigger.next(false);
        if (!this.isTemplateValid(request))
            throw new Error("Invalid update request");

        if (!request.mailingListId) {
            let emptyMailingList = new UpsertMailingListRequest({
                mailingListId: Guid.newGuid(),
                mailReceivers: [],
            });
            await this.mailingListClient
                .upsertMailingList(emptyMailingList)
                .toPromise();
            request.mailingListId = emptyMailingList.mailingListId;
        }

        var response = await this.projectClient
            .upsertTemplate(request)
            .toPromise();
        this.reloadProject(request.projectId);
        this.projectTemplateTrigger.next(true);
        return response;
    }

    getProjectDetails(
        projectId: string
    ): Observable<GetProjectDetailsResponse | undefined> {
        let projectDetailsObservable =
            this.projectsDetailsByProjectId[projectId];
        if (projectDetailsObservable) return projectDetailsObservable;

        let projectTrigger = this.getProjectTrigger(projectId);

        projectDetailsObservable = projectTrigger.pipe(
            switchMap((_) =>
                merge(
                    of(undefined),
                    this.projectClient.getProjectDetails(projectId)
                )
            ),
            publishReplay(2),
            refCount()
        );

        this.projectsDetailsByProjectId[projectId] = projectDetailsObservable;

        return projectDetailsObservable;
    }

    private getProjectTrigger(projectId: string): BehaviorSubject<undefined> {
        let projectTrigger = this.projectTriggers[projectId];
        if (!projectTrigger) {
            projectTrigger = new BehaviorSubject(undefined);
            this.projectTriggers[projectId] = projectTrigger;
        }
        return projectTrigger;
    }

    newComponent(projectId: string): UpsertComponentRequest {
        return new UpsertComponentRequest({
            projectId: projectId,
            componentId: Guid.newGuid(),
            batchSize: 1,
            comment: "",
            description: "",
            serialNumber: "",
            setNumber: "",
            volumeM3: 0,
            weightKg: 0,
        });
    }

    upsertComponents(components: ComponentStatus[]): Promise<any> {
        let updatePromises: Promise<any>[] = components.map((component) => {
            if (component.status === "deleted") {
                return this.componentClient
                    .deleteComponent(component.request.componentId)
                    .toPromise();
            } else if (component.status === "changedOrNew") {
                return this.componentClient
                    .upsertComponent(component.request)
                    .toPromise();
            } else if (component.status === "unchanged") {
                return Promise.resolve();
            } else {
                throw new Error(
                    "Unknown Component status: " + component.status
                );
            }
        });
        let projectIds: { [projectId: string]: true } = {};
        for (let c of components) projectIds[c.request.projectId] = true;

        this.reloadProject(...Object.keys(projectIds));

        return Promise.all(updatePromises);
    }

    async getComponents(projectId: string): Promise<ComponentStatus[]> {
        let projectDetails = await this.projectClient
            .getProjectDetails(projectId)
            .toPromise();
        let result = projectDetails.components.map((comp) => {
            let request = new UpsertComponentRequest({
                ...comp,
                componentId: comp.id,
                projectId: projectDetails.id,
            });
            let status = new ComponentStatus(request, "unchanged");
            return status;
        });
        return result;
    }

    reloadProject(...projectIds: string[]) {
        this.allTrigger.next(true);
        for (let projectId of projectIds) {
            let pTrigger = this.projectTriggers[projectId];
            if (pTrigger) {
                pTrigger.next(undefined);
            }
        }
    }

    getComments(projectId: string): Observable<ProjectComment[] | undefined> {
        let commentsObservable = this.commentsByProjectId[projectId];
        if (commentsObservable) {
            return commentsObservable;
        }
        let projTrigger = this.getProjectTrigger(projectId);

        commentsObservable = projTrigger.pipe(
            switchMap((_) =>
                merge(
                    of(undefined),
                    this.commentClient
                        .getProjectComments(projectId)
                        .pipe(map((response) => response.projectComments))
                )
            ),
            publishReplay(2),
            refCount()
        );

        this.commentsByProjectId[projectId] = commentsObservable;
        return commentsObservable;
    }

    async upsertComment(
        request: {
            id: string;
            projectId: string;
            eventId?: string;
            comment: string;
            timestamp: Date;
        }
    ): Promise<void> {

        const dto = new UpsertProjectCommentRequest({
            id: request.id,
            projectId: request.projectId,
            eventId: request.eventId,
            comment: request.comment,
            timestamp: request.timestamp,
            shouldSendDamageReport: false
        });

        await this.commentClient
            .upsertComment(dto)
            .toPromise();

        this.reloadProject(request.projectId);
    }

    async deleteProject(projectId: string) {
        this.allTrigger.next(false)
        await this.projectClient.deleteProject(projectId).toPromise();

        this.reloadProject(projectId);
    }

    async deleteTemplate(projectId: string) {
        this.projectTemplateTrigger.next(false);
        await this.projectClient.deleteProject(projectId).toPromise();
        this.projectTemplateTrigger.next(true);
    }
}
